import React from 'react';
import { Form, Input, Button, Typography, useToast } from '@crisellevaldez/speculo-ui';
import { Save } from 'lucide-react';
import { Organization } from '../../../../types/organization.types';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

// Define schema for Follow Up Boss subdomain
const subdomainSchema = z.object({
  subdomain: z
    .string()
    .min(1, 'Subdomain is required')
    .regex(/^[a-zA-Z0-9-]+$/, 'Subdomain can only contain letters, numbers, and hyphens')
    .refine((val) => !val.startsWith('-') && !val.endsWith('-'), {
      message: 'Subdomain cannot start or end with a hyphen',
    }),
});

type SubdomainFormData = z.infer<typeof subdomainSchema>;

interface FubSubdomainSetupProps {
  selectedOrg?: Organization;
  saveFubSubdomain: (orgId: string, subdomain: string) => Promise<boolean>;
  isSavingSubdomain: boolean;
  onSubdomainSaved: () => void;
}

export const FubSubdomainSetup: React.FC<FubSubdomainSetupProps> = ({
  selectedOrg,
  saveFubSubdomain,
  isSavingSubdomain,
  onSubdomainSaved,
}) => {
  const { showToast } = useToast();

  const form = useForm<SubdomainFormData>({
    resolver: zodResolver(subdomainSchema),
    defaultValues: {
      subdomain: selectedOrg?.fubSetup?.subdomain || '',
    },
  });

  const handleSave = async () => {
    if (!selectedOrg?.id) return;

    try {
      // Validate the form
      const isValid = await form.trigger();
      if (!isValid) return;

      const subdomain = form.getValues('subdomain');

      const success = await saveFubSubdomain(selectedOrg.id, subdomain);

      if (success) {
        showToast({
          message: 'Subdomain saved successfully',
          variant: 'success',
        });

        onSubdomainSaved();
      } else {
        showToast({
          message: 'Failed to save subdomain',
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error saving subdomain:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to save subdomain',
        variant: 'error',
      });
    }
  };

  return (
    <div>
      <Typography variant="p">
        Enter your Follow Up Boss subdomain to complete the integration setup. This will help you
        link speculo.ai Contacts to FUB.
      </Typography>

      <form className="space-y-4 mt-4">
        <Form.Control required>
          <Form.Label>Subdomain</Form.Label>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Input
                {...form.register('subdomain')}
                placeholder="speculo"
                error={form.formState.errors.subdomain?.message}
                disabled={isSavingSubdomain}
              />
            </div>
            <Typography variant="small" className="text-gray-500 dark:text-gray-400 whitespace-nowrap">
              .followupboss.com
            </Typography>
          </div>
          <Form.HelperText>
            Example: <span className="font-medium">speculo</span>.followupboss.com
          </Form.HelperText>
        </Form.Control>

        <div className="flex justify-end">
          <Button
            variant="primary"
            type="button"
            onClick={handleSave}
            disabled={isSavingSubdomain}
            isLoading={isSavingSubdomain}
            className="flex items-center gap-1 h-8"
          >
            <Save className="h-4 w-4" />
            Save Subdomain
          </Button>
        </div>
      </form>
    </div>
  );
};
